import {
    areaDescriptionEntity,
    areaDescriptionInputSchema,
    diaryEntity,
    diaryInputSchema,
    drawingSheetEntity,
    drawingSheetInputSchema,
    findEntity,
    findInputSchema,
    overallDescriptionEntity,
    overallDescriptionInputSchema,
    photoEntity,
    photoInputSchema,
    sampleEntity,
    sampleInputSchema,
    type AreaDescriptionDocType,
    type DiaryDocType,
    type DrawingSheetDocType,
    type FindDocType,
    type OverallDescriptionDocType,
    type PhotoDocType,
    type SampleDocType
} from '@ast-data/shared';
import type { RecordGridConfig } from '../../components/useRecordGrid';
import { HIDDEN_FINDING_FILTER_COLUMNS } from './findingRefLabels';
import {
    useInlineDiaryFields,
    HIDDEN_DIARY_DETAIL_COLUMNS
} from './useDiaryColumns';
import {
    useInlineDrawingSheetFields,
    HIDDEN_DRAWING_SHEET_DETAIL_COLUMNS
} from './useDrawingSheetsColumns';
import {
    useInlineFindFields,
    HIDDEN_FIND_DETAIL_COLUMNS
} from './useFindsColumns';
import {
    useInlineSampleFields,
    HIDDEN_SAMPLE_DETAIL_COLUMNS
} from './useSamplesColumns';
import {
    useInlineOverallDescriptionFields,
    HIDDEN_OVERALL_DESCRIPTION_DETAIL_COLUMNS
} from './useOverallDescriptionsColumns';
import {
    useInlineAreaDescriptionFields,
    HIDDEN_AREA_DESCRIPTION_DETAIL_COLUMNS
} from './useAreaDescriptionsColumns';
import {
    useInlinePhotoFields,
    HIDDEN_PHOTO_DETAIL_COLUMNS
} from './usePhotosColumns';
import { findHistoryLabel } from './findHistoryLabel';

// ---------------------------------------------------------------------------
/**
 * The five per-project record grids, defined once.
 *
 * Each entity has TWO screens — the project's own grid and its row in the
 * cross-project Auswertung — and both need the same nine facts about it: the
 * entity, its schema and CASL subject, its i18n namespace, its route, its key
 * column, which fields edit inline, and which columns ship hidden. Written out
 * per screen, that was ten places to keep in step, and the pair for one entity
 * sat in different folders where a change to one would not show up beside the
 * other.
 */
// ---------------------------------------------------------------------------

/**
 * The columns a Befund-referencing record hides: the filterable
 * Befundnummer/Fläche/Planum/Profil columns exist for the filter panel only.
 */
function hiddenWithRefs(detail: Record<string, boolean>) {
    return { ...HIDDEN_FINDING_FILTER_COLUMNS, ...detail };
}

export const PHOTO_GRID: RecordGridConfig<PhotoDocType> = {
    entity: photoEntity,
    schema: photoInputSchema,
    subject: 'Photo',
    labelPrefix: 'photo',
    routeSegment: 'photos',
    gridId: 'photos',
    keyField: 'fileName',
    useInlineFields: useInlinePhotoFields,
    hiddenColumns: hiddenWithRefs(HIDDEN_PHOTO_DETAIL_COLUMNS),
    dateLinkField: 'recordedAt'
};

export const FIND_GRID: RecordGridConfig<FindDocType> = {
    entity: findEntity,
    schema: findInputSchema,
    subject: 'Find',
    labelPrefix: 'find',
    routeSegment: 'finds',
    gridId: 'finds',
    keyField: 'findSlipNumber',
    historyTitle: findHistoryLabel,
    useInlineFields: useInlineFindFields,
    hiddenColumns: hiddenWithRefs(HIDDEN_FIND_DETAIL_COLUMNS),
    dateLinkField: 'recordedAt',
    findingLinkField: 'findingRefNumber'
};

export const SAMPLE_GRID: RecordGridConfig<SampleDocType> = {
    entity: sampleEntity,
    schema: sampleInputSchema,
    subject: 'Sample',
    labelPrefix: 'sample',
    routeSegment: 'samples',
    gridId: 'samples',
    // No number of its own: a Probe is found by its day, newest first.
    keyField: 'recordedAt',
    useInlineFields: useInlineSampleFields,
    hiddenColumns: hiddenWithRefs(HIDDEN_SAMPLE_DETAIL_COLUMNS),
    sortModel: [{ field: 'recordedAt', sort: 'desc' }],
    dateLinkField: 'recordedAt',
    mediaOwnerType: 'sample'
};

export const DRAWING_SHEET_GRID: RecordGridConfig<DrawingSheetDocType> = {
    entity: drawingSheetEntity,
    schema: drawingSheetInputSchema,
    subject: 'DrawingSheet',
    labelPrefix: 'drawingSheet',
    routeSegment: 'drawing-sheets',
    gridId: 'drawingSheets',
    keyField: 'drawingSheetNumber',
    useInlineFields: useInlineDrawingSheetFields,
    hiddenColumns: hiddenWithRefs(HIDDEN_DRAWING_SHEET_DETAIL_COLUMNS),
    dateLinkField: 'recordedAt'
};

export const OVERALL_DESCRIPTION_GRID: RecordGridConfig<OverallDescriptionDocType> =
    {
        entity: overallDescriptionEntity,
        schema: overallDescriptionInputSchema,
        subject: 'OverallDescription',
        labelPrefix: 'overallDescription',
        routeSegment: 'overall-descriptions',
        gridId: 'overallDescriptions',
        keyField: 'overallDescriptionNumber',
        useInlineFields: useInlineOverallDescriptionFields,
        hiddenColumns: hiddenWithRefs(
            HIDDEN_OVERALL_DESCRIPTION_DETAIL_COLUMNS
        ),
        dateLinkField: 'recordedAt',
        // The ticks serve the PDF export too, which a read-only reader is as
        // entitled to as anyone.
        checkboxSelection: 'always',
        mediaOwnerType: 'overallDescription'
    };

export const AREA_DESCRIPTION_GRID: RecordGridConfig<AreaDescriptionDocType> = {
    entity: areaDescriptionEntity,
    schema: areaDescriptionInputSchema,
    subject: 'AreaDescription',
    labelPrefix: 'areaDescription',
    routeSegment: 'area-descriptions',
    gridId: 'areaDescriptions',
    keyField: 'area',
    useInlineFields: useInlineAreaDescriptionFields,
    // References no Befund, so only its own detail columns hide.
    hiddenColumns: HIDDEN_AREA_DESCRIPTION_DETAIL_COLUMNS,
    // Fläche × Planum × Sondage is the key, so all three sort.
    sortModel: [
        { field: 'area', sort: 'asc' },
        { field: 'planum', sort: 'asc' },
        { field: 'sondage', sort: 'asc' }
    ],
    dateLinkField: 'recordedAt',
    // The ticks serve the PDF export too.
    checkboxSelection: 'always',
    mediaOwnerType: 'areaDescription'
};

export const DIARY_GRID: RecordGridConfig<DiaryDocType> = {
    entity: diaryEntity,
    schema: diaryInputSchema,
    subject: 'Diary',
    labelPrefix: 'diary',
    routeSegment: 'diaries',
    gridId: 'diary',
    keyField: 'recordedAt',
    useInlineFields: useInlineDiaryFields,
    // A Tagebuch day references no Befund, so only its own detail columns hide.
    hiddenColumns: HIDDEN_DIARY_DETAIL_COLUMNS,
    // Newest day first: the numbered records read forwards from the first one,
    // but a diary is worked on at its end.
    sortModel: [{ field: 'recordedAt', sort: 'desc' }],
    // No `?date=` link: this grid IS the day the others are filtered to.
    checkboxSelection: 'always',
    mediaOwnerType: 'diary'
};
